
import { computed, defineComponent, onMounted, reactive, watch } from 'vue'
import ymaps, { data } from 'yandex-maps'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import ModalPanel from '@/modules/NewDispatcher/components/ModalPanel/index.vue'

// @new-components
import NewMap from '@/common/new-components/NewMap/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

// @ui-kit
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'

// @module-components
import PlanningRecordItem from '@/modules/NewDispatcher/components/PlanningRecord/index.vue'
import PlanningCourierRecord from '@/modules/NewDispatcher/components/PlanningCourier/index.vue'
import ContainerForm from '@/modules/NewDispatcher/components/ContainerForm/index.vue'
import MoveInvoiceForm from '@/modules/NewDispatcher/components/MoveInvoiceForm/index.vue'
import AssignContainersModal from '@/modules/NewDispatcher/components/AssignContainersModal/index.vue'
import SetInvoicesWaveModal from '@/modules/NewDispatcher/components/SetInvoicesWaveModal/index.vue'
import AssemblyContainersModal from '@/modules/NewDispatcher/components/AssemblyContainersModal/index.vue'
import ContainerInvoicePanelInformation from '@/modules/NewDispatcher/components/ContainerInvoicePanelInformation/index.vue'

// @types
import {
    AssemblyContainersPayload,
    AssignCointainersToCourierPayload,
    AttachInvoicesToContainerPayload,
    ContainerPayload,
    GenerateContainerPayload,
    GetHoldRecordsParams,
    GetPlanningRecordsParams,
    HoldRecord,
    PlanningCourier,
    PlanningRecord,
    PlanningRecordContainerInvoice,
    PlanningRecordInvoice,
} from '@/new-services/types/planning'

import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import { Sector } from '@/new-services/types/sectors'
import { Wave } from '@/new-services/types/waves'
import { Provider } from '@/new-services/types/couriers'

// @new-services
import planningAPI from '@/new-services/planning.service'
import sectorsAPI from '@/new-services/sectors.service'
import wavesAPI from '@/new-services/waves.service'
import invoiceAPI from '@/new-services/invoice.service'
import couriersAPI from '@/new-services/couriers.service'

// @composable
import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// @validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'
import { UpdateCargoPayload } from '@/new-services/types/invoice'

const TAKE_TYPE = 2

import { LtlStatusColors } from '@/core/constants/columns/LTL.columns'

export default defineComponent({
    components: {
        PageTitle,
        NewButton,
        Loader,
        NewInput,
        NewSelect,
        NewMap,
        'planning-record': PlanningRecordItem,
        PlanningCourierRecord,
        ContainerForm,
        MoveInvoiceForm,
        AssignContainersModal,
        AssemblyContainersModal,
        ContainerInvoicePanelInformation,
        SetInvoicesWaveModal,
        ModalPanel,
        NewCheckbox,
    },
    setup() {
        const new_dispatcher_store = useNewDispatcherStore()

        const { havePermissions } = useAccount()

        const map_graphics = useMapGeoObjects()
        const { clearMap, addGeoObject, setMapCenter } = useMap()

        let map_instance = null as ymaps.Map | null

        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_map_opened: true,
            //
            is_hold_loading: false,
            is_hold_failed: false,
            //
            records: [] as PlanningRecord[],
            hold_records: [] as HoldRecord[],
            selected_invoices: [] as number[],
        })

        const invoice_state = reactive({
            is_opened: false,
            is_loading: false,
            invoice: null as PlanningRecordInvoice | null,
        })

        const couriers_state = reactive({
            is_loading: false,
            is_failed: false,
            couriers: [] as PlanningCourier[],
        })

        const filters_state = reactive({
            waveId: null as number | null,
            sectorIds: [] as number[],
            date: null as string | null,
            invoiceNumber: null as string | null,
        })

        const maps_state = reactive({
            is_checked: false,
        })

        const filters$ = useVuelidate(
            {
                waveId: { required },
                date: { required },
            },
            filters_state
        )

        const data_state = reactive({
            sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Sector[],
            },
            waves: {
                is_loading: false,
                is_failed: false,
                list: [] as Wave[],
            },
        })

        const providers_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Provider[],
        })

        const new_container_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const generate_state = reactive({
            is_loading: false,
        })

        const container_state = reactive({
            is_opened: false,
            is_loading: false,
            invoiceIds: [] as number[],
            invoice: null as PlanningRecordInvoice | PlanningRecordContainerInvoice | null,
        })

        const assign_containers_state = reactive({
            is_opened: false,
            is_loading: false,
            containersIds: [] as number[],
        })

        const assembly_containers_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const set_invoices_wave_state = reactive({
            is_opened: false,
            invoiceIds: [] as number[],
        })

        const loadData = () => {
            new_dispatcher_store.validation$.$touch()
            filters$.value.$touch()

            if (filters$.value.$invalid || new_dispatcher_store.validation$.$invalid) return

            clearMap(map_instance as ymaps.Map)

            getPlanningRecords()
            getHoldRecords()

            loadCouriers(new_dispatcher_store.dispatcherSectorIds)
            if (new_dispatcher_store.dispatcherSectorIds.length) {
                loadSectors(new_dispatcher_store.dispatcherSectorIds)
            }
        }

        const generateContainer = async () => {
            new_dispatcher_store.validation$.$touch()
            filters$.value.$touch()

            if (filters$.value.$invalid || new_dispatcher_store.validation$.$invalid) return

            const answer = confirm('Вы действительно хотите сформировать контейнеры?')
            if (!answer) return

            try {
                generate_state.is_loading = true

                const payload: GenerateContainerPayload = {
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    waveId: filters_state.waveId as number,
                    sectorIds: filters_state.sectorIds,
                    statusId: 1,
                    date: filters_state.date as string,
                }

                await planningAPI.generateContainer(payload)

                notifications.notify({ type: 'success', title: 'Контейнеры сформированы' })
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сформировании контейнеров произошла ошибка',
                })
            } finally {
                generate_state.is_loading = false
            }
        }

        const startMoveInvoiceToContainer = (invoiceIds: number[]) => {
            container_state.invoiceIds = invoiceIds
            container_state.is_opened = true
        }

        const startSetInvoicesWave = (invoiceIds: number[]) => {
            set_invoices_wave_state.invoiceIds = invoiceIds
            set_invoices_wave_state.is_opened = true
        }

        const cancelSetInvoicesWave = () => {
            set_invoices_wave_state.is_opened = false
            set_invoices_wave_state.invoiceIds = []
        }

        const onSubmitSetInvoiceWave = () => {
            cancelSetInvoicesWave()
            loadData()
        }

        const deleteInvoiceFromContainer = async ({
            invoiceIds,
            containerId,
        }: {
            invoiceIds: number[]
            containerId: number
        }) => {
            try {
                state.is_loading = true

                await planningAPI.deleteInvoiceFromContainer(containerId, invoiceIds)

                notifications.notify({
                    type: 'success',
                    title: `Накладная удалена из контейнера`,
                })
                manageContainerCancel()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При удалении накладной из контейнера произошла ошибка`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const deleteContainer = async (containerId: number) => {
            try {
                state.is_loading = true

                await planningAPI.deleteContainer(containerId)

                notifications.notify({
                    type: 'success',
                    title: 'Контейнер удален',
                })
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При удалении контейнера произошла ошибка`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const manageContainerCancel = () => {
            container_state.is_opened = false
        }

        const manageContainerSubmit = async ({
            containerId,
            payload,
        }: {
            containerId: number
            payload: AttachInvoicesToContainerPayload
        }) => {
            try {
                container_state.is_loading = true

                await planningAPI.attachInvoicesToContainer(containerId, payload)

                notifications.notify({
                    type: 'success',
                    title: `Накладные перемещены в контейнер`,
                })
                manageContainerCancel()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При перемещении накладных в контейнер произошла ошибка`,
                })
            } finally {
                container_state.is_loading = false
            }
        }

        const startAssignContainersToCourier = (containersIds: number[]) => {
            new_dispatcher_store.validation$.$touch()
            if (new_dispatcher_store.validation$.$invalid) return

            assign_containers_state.containersIds = containersIds
            assign_containers_state.is_opened = true
        }

        const cancelAssignContainersToCourier = () => {
            assign_containers_state.is_opened = false
            assign_containers_state.containersIds = []
        }

        const assignContainersToCourier = async (payload: AssignCointainersToCourierPayload) => {
            try {
                assign_containers_state.is_loading = true

                await planningAPI.assignContainersToCourier(payload)

                notifications.notify({ title: 'Контейнеры назначены', type: 'success' })
                cancelAssignContainersToCourier()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При назначении контейнеров на курьера произошла ошибка',
                })
            } finally {
                assign_containers_state.is_loading = false
            }
        }

        const startAssemblyContainers = () => {
            filters$.value.$touch()
            if (filters$.value.$invalid) return

            assembly_containers_state.is_opened = true
        }

        const cancelAssemblyContainers = () => {
            assembly_containers_state.is_opened = false
        }

        const assemblyContainers = async (payload: AssemblyContainersPayload) => {
            try {
                assembly_containers_state.is_loading = true

                await planningAPI.assemblyContainers(payload)

                notifications.notify({ title: 'Контейнеры отправлены на сборку', type: 'success' })
                cancelAssemblyContainers()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При отправке контейнеров на сборку произошла ошибка',
                })
            } finally {
                assembly_containers_state.is_loading = false
            }
        }

        const startCreateNewContainer = () => {
            new_dispatcher_store.validation$.$touch()
            filters$.value.$touch()

            if (filters$.value.$invalid || new_dispatcher_store.validation$.$invalid) return

            new_container_state.is_opened = true
        }

        const cancelCreateNewContainer = () => {
            new_container_state.is_opened = false
            state.selected_invoices = []
        }

        const createContainer = async (payload: ContainerPayload) => {
            try {
                new_container_state.is_loading = true

                const params = {
                    ...payload,
                    invoiceIds: state.selected_invoices,
                }
                await planningAPI.createContainer(params)

                notifications.notify({ title: 'Контейнер добавлен', type: 'success' })
                cancelCreateNewContainer()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении контейнера произошла ошибка',
                })
            } finally {
                new_container_state.is_loading = false
            }
        }

        const getPlacemark = (invoice: PlanningRecordInvoice | PlanningRecordContainerInvoice) => {
            const coordinates = [Number(invoice.latitude), Number(invoice.longitude)]

            const props = {
                balloonContentHeader: `<div class="balloon__header">${invoice.invoiceNumber}</div>`,
                balloonContentBody: `<div class="balloon__body mt-2">
                    <p>№ Накладной: ${invoice.invoiceNumber}</p>
                    <p>Кол-во мест: ${invoice.places}</p>
                    <p>Физ. вес: ${invoice.weight}</p>
                    <p>Объемный вес: ${invoice.volumeWeight}</p>
                </div>`,
            }

            return map_graphics.createPoint(
                coordinates,
                {
                    iconContent: invoice.invoiceNumber,
                    balloonContentHeader: props.balloonContentHeader,
                    balloonContentBody: props.balloonContentBody,
                },
                {
                    preset: 'islands#blueStretchyIcon',
                }
            )
        }

        const renderInvoices = (records: PlanningRecord[]) => {
            const cluster = map_graphics.createCluster({
                preset: 'islands#invertedBlueClusterIcons',
                gridSize: 80,
                maxZoom: 21,
            })

            let placemarks = [] as any[]

            records.forEach((record) => {
                record.invoices.forEach((invoice) => {
                    placemarks.push(getPlacemark(invoice))
                })

                if (record.containers) {
                    record.containers.forEach((container) => {
                        container.invoices.forEach((container_invoice) => {
                            placemarks.push(getPlacemark(container_invoice))
                        })
                    })
                }
            })

            cluster.options.set({ clusterDisableClickZoom: true })
            cluster.add(placemarks)

            addGeoObject(map_instance as ymaps.Map, cluster)
        }

        const editDefaultRecords = () => {
            if (!state.records.length) return

            state.records.forEach((record) => {
                record.invoices.forEach((invoice) => {
                    if (invoice.problems && invoice.problems.length) {
                        invoice.isRed = true
                    } else {
                        invoice.isRed = false
                    }
                })

                record.containers.forEach((container) => {
                    container.invoices.forEach((invoice) => {
                        if (invoice.problems && invoice.problems.length) {
                            invoice.isRed = true
                        } else {
                            invoice.isRed = false
                        }
                    })
                })
            })
        }

        const getPlanningRecords = async () => {
            new_dispatcher_store.validation$.$touch()
            if (new_dispatcher_store.validation$.$invalid) return

            try {
                state.is_loading = true

                const params: GetPlanningRecordsParams = {
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    waveId: filters_state.waveId as number,
                    sectorIds: filters_state.sectorIds,
                    statusId: 1,
                    invoiceNumber: filters_state.invoiceNumber,
                    date: filters_state.date as string,
                }

                const response = await planningAPI.getPlanningRecords(params)
                state.records = response.data.data.filter((record) => record.invoicesCount)
                editDefaultRecords()

                renderInvoices(response.data.data)

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении данных планирования произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const getHoldRecords = async () => {
            new_dispatcher_store.validation$.$touch()
            if (new_dispatcher_store.validation$.$invalid) return

            try {
                state.is_hold_loading = true

                const params: GetHoldRecordsParams = {
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    date: filters_state.date as string,
                }

                const response = await planningAPI.getHoldRecords(params)

                state.hold_records = [{ ...response.data.data }]

                renderInvoices([{ ...response.data.data }])

                state.is_hold_failed = false
            } catch (error) {
                console.error(error)
                state.is_hold_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении данных планирования произошла ошибка',
                })
            } finally {
                state.is_hold_loading = false
            }
        }

        const loadCouriers = async (dispatcher_sector_ids: number[]) => {
            filters$.value.$touch()
            if (filters$.value.$invalid) return

            try {
                couriers_state.is_loading = true

                const response = await planningAPI.getPlanningCouriers({
                    waveId: filters_state.waveId as number,
                    date: filters_state.date as string,
                    dispatcherSectorIds: dispatcher_sector_ids,
                })

                couriers_state.couriers = response.data.data

                couriers_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка курьеров произошла ошибка',
                })
                couriers_state.is_failed = true
            } finally {
                couriers_state.is_loading = false
            }
        }

        const onResetData = () => {
            filters_state.date = null
            filters_state.sectorIds = []
            filters_state.invoiceNumber = null
            filters_state.waveId = null

            filters$.value.$reset()

            clearMap(map_instance as ymaps.Map)
            state.records = []
        }

        const drawSector = (sector: Sector) => {
            const polygon = map_graphics.createPolygon({
                coordinates: JSON.parse(sector.coordinates),
                color: sector.color,
                hintContent: `Сектор ${sector.name}`,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)
        }

        const drawSectors = (sectors: Sector[]) => {
            if (!map_instance) return
            sectors.forEach(drawSector)
        }

        const loadSectors = async (dispatcher_sector_ids: number[]) => {
            try {
                data_state.sectors.is_loading = true

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds: dispatcher_sector_ids,
                })

                data_state.sectors.list = response.data.data
                drawSectors(data_state.sectors.list)

                data_state.sectors.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка секторов произошла ошибка',
                })
                data_state.sectors.is_failed = true
            } finally {
                data_state.sectors.is_loading = false
            }
        }

        const loadWaves = async (dispatcher_sector_ids: number[]) => {
            try {
                data_state.waves.is_loading = true

                const response = await wavesAPI.getWaves({
                    dispatcherSectorIds: dispatcher_sector_ids,
                })
                data_state.waves.list = response.data.data

                data_state.waves.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка волн произошла ошибка',
                })
                data_state.waves.is_failed = true
            } finally {
                data_state.waves.is_loading = false
            }
        }

        const reloadSectors = () => {
            if (!new_dispatcher_store.dispatcherSectorIds.length) return
            loadSectors(new_dispatcher_store.dispatcherSectorIds)
        }

        const reloadWaves = () => {
            if (!new_dispatcher_store.dispatcherSectorIds.length) return
            loadWaves(new_dispatcher_store.dispatcherSectorIds)
        }

        const reloadCouriers = () => {
            if (!new_dispatcher_store.dispatcherSectorIds.length) return
            loadCouriers(new_dispatcher_store.dispatcherSectorIds)
        }

        const setMapCenterOnDispatcherSector = (sector: DispatcherSector) => {
            if (sector.city) {
                if (sector.city.latitude && sector.city.longitude) {
                    setMapCenter(map_instance as ymaps.Map, [
                        sector.city.latitude,
                        sector.city.longitude,
                    ])
                    return
                }
            }

            const centroid = map_graphics.getPolygonCentroid(JSON.parse(sector.coordinates))
            setMapCenter(map_instance as ymaps.Map, centroid)
        }

        const reset = () => {
            onResetData()

            data_state.sectors.list = []
            data_state.waves.list = []
            couriers_state.couriers = []
        }

        const onDispatcherSectorChange = (dispatcher_sector_ids: number[]) => {
            if (!dispatcher_sector_ids.length) {
                reset()
                return
            }

            clearMap(map_instance as ymaps.Map)

            loadSectors(dispatcher_sector_ids)
            loadWaves(dispatcher_sector_ids)
            loadCouriers(dispatcher_sector_ids)

            // if (new_dispatcher_store.dispatcherSectors.length) {
            //     setMapCenterOnDispatcherSector(new_dispatcher_store.dispatcherSectors)
            // }
        }

        const onMapReady = (instance: ymaps.Map) => {
            map_instance = instance

            if (new_dispatcher_store.dispatcherSectorIds.length) {
                onDispatcherSectorChange(new_dispatcher_store.dispatcherSectorIds)
            }
        }

        const openInvoicePanel = (invoice: PlanningRecordInvoice) => {
            invoice_state.invoice = { ...invoice }
            invoice_state.is_opened = true
        }

        const closeInvoicePanel = () => {
            invoice_state.is_opened = false
            invoice_state.invoice = null
        }

        const updateInvoice = async ({
            invoice_id,
            payload,
        }: {
            invoice_id: number
            payload: UpdateCargoPayload
        }) => {
            try {
                invoice_state.is_loading = true

                await invoiceAPI.updateCargoPayload(invoice_id, payload)

                notifications.notify({ type: 'success', title: 'Данные накладной сохранены' })
                closeInvoicePanel()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении данных накладной произошла ошибка',
                })
            } finally {
                invoice_state.is_loading = false
            }
        }

        const renderInvoicesWithoutContainer = () => {
            clearMap(map_instance as ymaps.Map)
            drawSectors(data_state.sectors.list)

            // накладные без контейнеров
            const records = state.records.map((record) => {
                return { ...record, containers: [] }
            })
            const hold_records = state.hold_records.map((record) => {
                return { ...record, containers: [] }
            })

            renderInvoices([...records, ...hold_records])
        }

        const onMapCheckboxChange = (is_checked: boolean) => {
            maps_state.is_checked = is_checked

            if (!is_checked) {
                clearMap(map_instance as ymaps.Map)
                drawSectors(data_state.sectors.list)

                renderInvoices(state.records)
                renderInvoices(state.hold_records)
            } else if (state.records.length || state.hold_records.length) {
                renderInvoicesWithoutContainer()
            }
        }

        const toggleMapAndCouriers = () => {
            state.is_map_opened = !state.is_map_opened
        }

        const loadProviders = async () => {
            try {
                providers_state.is_loading = true
                state.is_loading = true

                const params = {
                    type_id: TAKE_TYPE,
                }

                const response = await couriersAPI.getProviders(params)
                providers_state.list = response.data.data

                providers_state.is_failed = false
            } catch (error) {
                console.error(error)
                providers_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка поставщиков произошла ошибка',
                })
            } finally {
                providers_state.is_loading = false
                state.is_loading = false
            }
        }

        const selectInvoice = (id: number) => {
            if (!state.selected_invoices.includes(id)) {
                state.selected_invoices.push(id)
            }
        }

        const unselectInvoice = (id: number) => {
            const index = state.selected_invoices.findIndex((invoice_id) => invoice_id === id)
            if (index >= 0) {
                state.selected_invoices.splice(index, 1)
            }
        }

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectorIds,
            (new_value) => {
                onDispatcherSectorChange(new_value)
            }
        )

        //

        const isCanCreateContainer = computed(() =>
            havePermissions(['dispatcher.container-create', 'all-root-permission'])
        )
        const isCanAssemblyContainers = computed(() =>
            havePermissions(['dispatcher.container-send-assembly', 'all-root-permission'])
        )
        const isMapCheckboxDisabled = computed(() => !state.records.length)
        const getBtnClass = computed(
            () => `fas ${state.is_map_opened ? 'fa-eye-slash' : 'fa-eye'} mr-2`
        )
        const getBtnText = computed(() =>
            state.is_map_opened
                ? 'Скрыть карту и данные курьеров'
                : 'Показать карту и данные курьеров'
        )
        const getPlanningClass = computed(() => (state.is_map_opened ? 'w-1/2' : 'w-full'))
        const getModalTitle = computed(() => {
            if (invoice_state.invoice) return invoice_state.invoice.invoiceNumber
            return 'Информация'
        })
        const getModalStatus = computed(() => {
            if (invoice_state.invoice) return invoice_state.invoice.status
            return null
        })
        const getCheapClass = computed(() => {
            if (!invoice_state.invoice || !invoice_state.invoice.statusId) return ''

            const { statusId } = invoice_state.invoice

            if (LtlStatusColors[statusId]) return LtlStatusColors[statusId]
            else if (statusId === 51) return 'red'
            else if (![1, 4].includes(statusId)) return 'yellow'

            return ''
        })

        //

        onMounted(loadProviders)

        //

        return {
            new_dispatcher_store,
            state,
            invoice_state,
            couriers_state,
            filters_state,
            providers_state,
            data_state,
            filters$,
            new_container_state,
            container_state,
            assign_containers_state,
            assembly_containers_state,
            generate_state,
            maps_state,
            //
            generateContainer,
            //
            startMoveInvoiceToContainer,
            deleteInvoiceFromContainer,
            manageContainerCancel,
            manageContainerSubmit,
            //
            startAssignContainersToCourier,
            cancelAssignContainersToCourier,
            assignContainersToCourier,
            //
            startAssemblyContainers,
            cancelAssemblyContainers,
            assemblyContainers,
            //
            startCreateNewContainer,
            cancelCreateNewContainer,
            createContainer,
            //
            set_invoices_wave_state,
            startSetInvoicesWave,
            cancelSetInvoicesWave,
            onSubmitSetInvoiceWave,
            //
            loadData,
            onResetData,
            //
            onDispatcherSectorChange,
            reloadSectors,
            reloadWaves,
            reloadCouriers,
            //
            onMapReady,
            //
            openInvoicePanel,
            closeInvoicePanel,
            updateInvoice,
            deleteContainer,
            //
            onMapCheckboxChange,
            toggleMapAndCouriers,
            //
            isCanCreateContainer,
            isCanAssemblyContainers,
            isMapCheckboxDisabled,
            getBtnClass,
            getBtnText,
            getPlanningClass,
            selectInvoice,
            unselectInvoice,
            getModalTitle,
            getModalStatus,
            getCheapClass,
        }
    },
})
